import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import type { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import { toPascalCase } from 'components/WorkspaceEditor/utils'
import { ObjectBuilder } from './ObjectBuilder'

/**
 * Common type for the Text content elements mutation
 * !Note: These are the only props accepted by the mutation api, else, the mutation will throw an error
 */
export type TextContentElementMutation = Partial<
    Pick<
        TextContentElement,
        | 'id'
        | 'imperialUnits'
        | 'metricUnits'
        | 'optionItemState'
        | 'text'
        | 'type'
        | 'placeholderText'
        | 'changeType'
        | 'authoredUserId'
        | 'elements'
    > & {
        isPermanentlyDeleted?: boolean
    }
>

/** Base properties shared by input builders */
export interface InputBuilderProps {
    /** ID of the content item */
    contentItemId: string
    /** ID of the selected project element node */
    selectedProjectElementNodeId: string
    /** ID of the element */
    elementId: string
    /** Array of content item view nodes */
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    /** projectId from workspace url */
    projectId?: string
    /** isForArticle */
    isForArticle?: boolean
}

/**
 * Abstract base class for building input variables for updating project content item elements.
 * It encapsulates common logic and properties, requiring subclasses to implement specific behaviors.
 *
 * @template TInput The specific input type for the mutation (e.g., SetProjectContentItemElementsInput).
 * @template TProps The specific props type for the mutation variables (e.g., SetProjectContentItemElementsProps).
 * @template TData The specific data type used for building elements (e.g., string[] or Partial<TextContentElement>[]).
 */
export abstract class InputBuilder<TInput, TProps, TData> {
    protected contentItem: ContentItemViewNodesType | undefined
    protected contentItemId: string
    protected selectedProjectElementNodeId: string
    protected elementId: string
    protected contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    protected specificData: TData // Holds either specifierNotes or changedElements
    protected projectId?: string
    protected isForArticle?: boolean

    constructor(props: InputBuilderProps, specificData: TData) {
        this.contentItemId = props.contentItemId
        this.selectedProjectElementNodeId = props.selectedProjectElementNodeId
        this.elementId = props.elementId
        this.contentItemViewNodes = props.contentItemViewNodes
        this.specificData = specificData
        this.isForArticle = props.isForArticle
        this.projectId = props.projectId
    }

    /**
     * Abstract method to retrieve the specific content item node.
     * Implementation varies based on how the content item is identified.
     * @param contentItemId - The ID of the content item.
     * @returns The view node containing the content item.
     */
    protected abstract getContentItem(
        contentItemId: string
    ): ContentItemViewNodesType

    /**
     * Retrieves the parent type of the node from global store.
     * @param dtvParentId - The dtvItemId of the parent node.
     * @returns The parent's type converted to PascalCase.
     */
    protected getParentType = (dtvParentId: string): string => {
        const parentNode = this.contentItemViewNodes.find(
            (n) => n.dtvItemId === dtvParentId
        )
        return toPascalCase(parentNode?.type || '')
    }

    /**
     * Abstract method to retrieve the root article node.
     * Implementation may differ based on the structure or specific requirements.
     * @param node - The node to start the search from.
     * @returns The root article node.
     */
    protected abstract getRootArticle(
        node: Partial<ContentItemViewNodesType> | undefined
    ): Partial<ContentItemViewNodesType> | undefined

    /**
     * Replaces an element in the target array with a new value.
     * Supports replacing a single object or an array of objects.
     *
     * @param target - The original array of elements.
     * @param replacement - A single object or an array of objects to replace the element.
     * @returns A new array with the replacement applied.
     */
    protected replaceInArray = <T extends Partial<{ id: string }>>(
        target: T[],
        replacement: T | T[]
    ): T[] => {
        const index = target.findIndex((obj) => obj.id === this.contentItemId)

        let newArray = [...target]

        if (index !== -1) {
            if (Array.isArray(replacement)) {
                newArray.splice(index, 1, ...replacement)
            } else {
                newArray[index] = replacement
            }
        }

        return newArray
    }

    /**
     * Helper that resolves null changeType by using (current) contentItem's changeType value
     * - use parent's changeType if element.changeType is null/undefined
     * @param element - The text element to resolve
     * @returns The TextContentElement with resolved changeType property
     * */
    protected resolveChangeType = (element: Partial<TextContentElement>) => {
        // Find the specific element within the contentItem's elements array
        const parentElement = this.contentItem?.elements?.find(
            (el) => el.id === element.id
        )
        return element?.changeType || parentElement?.changeType
    }

    /**
     * Resolves an object's properties based on specific rules:
     * - Replaces `null` or `undefined` values with an empty string (`''`).
     * - Keeps `false` boolean values as `false`.
     * - Leaves all other values unchanged.
     *
     * @template T - The type of the object.
     * @param {T | Partial<T>} element - The object to resolve.
     * @returns {T} A new object with resolved property values.
     */
    protected nullOrUndefinedResolver = <T extends Record<string, any>>(
        element: T | Partial<T>
    ): T => {
        return Object.entries(element).reduce((acc, [key, value]) => {
            let resolvedValue: any

            if (value === null || value === undefined) {
                resolvedValue = ''
            } else if (value === false) {
                resolvedValue = false
            } else {
                resolvedValue = value
            }

            return {
                ...acc,
                [key]: resolvedValue,
            }
        }, {} as T)
    }

    /**
     * Creates an element mutation object (recursively maps elements).
     *
     * !Note: These are the only props accepted by the mutation api, else, the mutation will throw an error
     *
     * @param element - The element to transform.
     * @returns {TextContentElementMutation} A TextContentElementMutation object.
     */
    protected toMutationModel = <T extends Partial<TextContentElement>>(
        element: T
    ): TextContentElementMutation => {
        const builder = new ObjectBuilder<TextContentElementMutation>()
            .set('id', element.id)
            .set('text', element.text)
            .set('changeType', this.resolveChangeType(element)) // resolve changeType separately
            .set('elements', this.mapElements(element))
            .set(
                'authoredUserId',
                element.authoredUserId?.trim()
                    ? element.authoredUserId
                    : 'anonymous1'
            )
            .set('type', element.type)
            .set('imperialUnits', element.imperialUnits)
            .set('metricUnits', element.metricUnits)
            .set('optionItemState', element.optionItemState) // TODO: resolve separately, find out what exact types
            .set('placeholderText', element.placeholderText)

        if (
            'isPermanentlyDeleted' in element &&
            element.isPermanentlyDeleted !== undefined
        ) {
            builder.set(
                'isPermanentlyDeleted',
                element.isPermanentlyDeleted as boolean
            )
        }

        return builder.build()
    }

    /**
     * Maps the elements of a content item to an array of mutation-ready objects.
     *
     * @param contentItem - The content item whose elements are mapped.
     * @returns An array of TextContentElementMutation.
     */
    protected mapElements = <T extends Partial<TextContentElement>>(
        contentItem: T
    ): TextContentElementMutation[] => {
        const elements = contentItem.elements || []

        return elements.map(this.toMutationModel.bind(this))
    }

    /**
     * Abstract method to build the final array of elements for the mutation input.
     * This involves potentially replacing elements and mapping them to the mutation model.
     * @returns An array of TextContentElementMutation.
     */
    protected abstract buildElements(): TextContentElementMutation[]

    /**
     * Abstract method to build the specific 'input' object for the mutation variables.
     * @param builder - An ObjectBuilder instance for the input type TInput.
     * @returns The builder instance populated with input data.
     */
    protected abstract buildInput(
        builder: ObjectBuilder<
            TInput extends Record<string, any> ? TInput : never
        >
    ): ObjectBuilder<TInput extends Record<string, any> ? TInput : never>

    /**
     * Abstract method to build the final mutation variables object.
     * @returns The complete variables object of type TProps.
     */
    abstract build(): TProps
}
